import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchFromPaymentsAPI, parseBackendDate } from '../services/common.js';
import { getPracticeInfo } from '../services/paymentsService.js';
import DateRangePicker from './DateRangePicker.js';

// ---------------------------------------------------------------------------
// ReportTab — a reconciliation report listing every PAID invoice for a
// practice with the money breakdown an office manager needs to tie out
// against their bank deposits and PMS ledger:
//
//   Invoice Amount (base) → Surcharge → Total Charged → Processing Fee →
//   Net to Merchant, plus the card brand / last-4 / type and the PMS
//   reconciliation status.
//
// All figures are derived client-side from the same /api/payments/invoices
// payload the Invoices tab consumes — the webhook handler stamps the
// authoritative breakdown onto each invoice doc:
//   - total                       base invoice amount (dollars)
//   - surchargeApplied.*Cents     surcharge breakdown (when surcharged)
//   - actualPayinAmountCents      what the card was actually charged
//   - paymentCard.{brand,last_4,type}
//   - merchantFees                Rainforest processing fee (payin.processing)
//   - reconciliationStatus        PMS match state
//
// Processing fee == the platform billing fee Rainforest pulls from the
// gross (the payin_config's ``merchant_billing.fee``). Three cases mirror
// the backend money model:
//   - CREDIT + surcharge → the fee equals the surcharge the patient paid,
//     so the merchant nets the base (it's a wash).
//   - DEBIT (practice surcharging on) → a fixed ``debitCardBillingPct``
//     override (default 0.5% of base) replaces the merchant's profile
//     rate. See compute_debit_billing_fee in payment_methods.py — the
//     patient pays exactly base, the merchant eats the 0.5%.
//   - Otherwise → the merchant's profile rate, only knowable from the
//     ``merchantFees`` the payin.processing webhook stamps.
// Net to merchant is therefore always (total charged − processing fee).
// ---------------------------------------------------------------------------

// Statuses surfaced in the reconciliation report. "paid" = the payin
// settled (payin.succeeded); "processing" = the card was authorized and
// the payin is in flight (payin.authorized/processing) but not yet
// settled. Processing rows carry the merchant fee (stamped on
// payin.processing) but no paidAt yet.
const REPORT_STATUSES = new Set(['paid', 'processing']);

const statusLabels = {
  paid: { label: 'Paid', color: '#1d6b40', bg: '#e7f4ec' },
  processing: { label: 'Processing', color: '#a8520c', bg: '#fdf3e3' },
};

// Mirrors DEFAULT_DEBIT_CARD_BILLING_PCT in payments/backend/payment_methods.py.
const DEFAULT_DEBIT_CARD_BILLING_PCT = 0.5;

const usd = (cents) => {
  const dollars = (Number(cents) || 0) / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
};

// Pull integer cents out of a value that may be a bare number or a
// Rainforest money object (``{amount, currency_code}``). Returns null
// when neither shape is present.
const amountToCents = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);
  if (value && typeof value === 'object' && typeof value.amount === 'number') {
    return Math.round(value.amount);
  }
  return null;
};

// Best-effort parse of the Rainforest merchant fee into integer cents.
// `merchant_fees` (stamped on payin.processing) is most often the nested
// shape ``{total_amount: {amount: 706, currency_code: "USD"}}`` but can
// also arrive as a flat integer of cents — handle both, plus a generic
// sum-of-leaves fallback. Returns null when we can't read a fee so the UI
// can render "—" rather than a misleading $0.00.
const parseFeeCents = (merchantFees) => {
  if (merchantFees == null) return null;

  const direct = amountToCents(merchantFees);
  if (direct != null) return direct;

  if (typeof merchantFees === 'object') {
    const keys = [
      'total_amount',
      'total',
      'amount',
      'fee',
      'fees',
      'total_fees_amount',
      'total_payment_billing_fees_amount',
    ];
    for (const key of keys) {
      const cents = amountToCents(merchantFees[key]);
      if (cents != null) return cents;
    }
    let sum = 0;
    let found = false;
    for (const value of Object.values(merchantFees)) {
      const cents = amountToCents(value);
      if (cents != null) {
        sum += cents;
        found = true;
      }
    }
    if (found) return sum;
  }
  return null;
};

// Resolve the platform billing fee (Rainforest merchant_billing.fee) the
// merchant pays on a payin, in integer cents. Returns { cents, source }
// where source documents which rule applied, or cents: null when the fee
// is genuinely unknown (profile-rate payment whose webhook fee hasn't
// posted yet).
const resolveFeeCents = ({ invoice, surchargeCents, baseCents, cardType, settings }) => {
  // 1. Credit-card surcharge → the merchant_billing.fee equals the
  //    surcharge the patient paid; the merchant nets the base.
  if (surchargeCents > 0) {
    return { cents: surchargeCents, source: 'surcharge' };
  }

  // 2. Debit-card billing override — only when the practice has
  //    surcharging enabled (the same toggle gates BIN lookups), the card
  //    is DEBIT, and the configured pct is positive. Floor to whole cents
  //    exactly like compute_debit_billing_fee.
  const surchargeEnabled = !!(settings && settings.surchargeEnabled);
  if (surchargeEnabled && cardType === 'DEBIT') {
    // Prefer the practice's configured rate, fall back to the server's
    // default (debitCardBillingPctDefault), then the local constant.
    let pct;
    if (settings && settings.debitCardBillingPct != null) {
      pct = Number(settings.debitCardBillingPct);
    } else if (settings && settings.debitCardBillingPctDefault != null) {
      pct = Number(settings.debitCardBillingPctDefault);
    } else {
      pct = DEFAULT_DEBIT_CARD_BILLING_PCT;
    }
    if (!Number.isFinite(pct)) pct = DEFAULT_DEBIT_CARD_BILLING_PCT;
    pct = Math.max(0, Math.min(3, pct));
    if (pct > 0) {
      const computed = Math.floor((baseCents * pct) / 100);
      // Sub-cent floor: a 0¢ override is "no override" — fall through to
      // the webhook-reported profile fee instead of claiming $0.00.
      if (computed > 0) {
        return { cents: computed, source: 'debit_override', pct };
      }
    }
  }

  // 3. Otherwise the merchant pays their profile rate, only knowable from
  //    the fee Rainforest stamps on payin.processing.
  const webhookFee = parseFeeCents(invoice.merchantFees);
  if (webhookFee != null) {
    return { cents: webhookFee, source: 'webhook' };
  }

  return { cents: null, source: 'unknown' };
};

// Resolve the date the card was actually charged.
//
// The truest signal is the Rainforest payin's own ``created_at`` — when
// the charge was created/authorized. The dedicated ``payinCreatedAt``
// field is often null (the ``payin.created`` event isn't always stored),
// and ``payinUpdatedAt`` is the *succeeded* event's update time, which can
// lag the charge by hours (settlement / webhook redelivery). So we dig
// the payin ``created_at`` out of the stored webhook events — it's carried
// identically on every event (authorized/processing/succeeded) — and take
// the earliest. Falls back to our server-stamped timestamps only when no
// webhook carries a created_at.
const extractChargeAt = (invoice) => {
  if (invoice.payinCreatedAt) return invoice.payinCreatedAt;

  const hooks = Array.isArray(invoice.payinWebhooks) ? invoice.payinWebhooks : [];
  let earliest = null;
  for (const hook of hooks) {
    const createdAt = hook && hook.data && hook.data.data && hook.data.data.created_at;
    if (createdAt && (!earliest || createdAt < earliest)) {
      earliest = createdAt;
    }
  }
  if (earliest) return earliest;

  return invoice.paidAt || invoice.payinUpdatedAt || invoice.updatedAt || null;
};

// Collapse a paid invoice doc into the flat row the report renders +
// exports. All money is normalised to integer cents so the totals add up
// without float drift.
const toReportRow = (invoice, settings) => {
  const baseCents = Math.round((Number(invoice.total) || 0) * 100);

  const surcharge = invoice.surchargeApplied || {};
  const surchargeCents = Math.round(Number(surcharge.surchargeCents) || 0);

  // Prefer the amount Rainforest actually charged (covers the mid-checkout
  // surcharge swap). Fall back to base + surcharge, then to base.
  let chargedCents = null;
  if (typeof invoice.actualPayinAmountCents === 'number') {
    chargedCents = Math.round(invoice.actualPayinAmountCents);
  } else if (typeof surcharge.totalCents === 'number') {
    chargedCents = Math.round(surcharge.totalCents);
  } else {
    chargedCents = baseCents + surchargeCents;
  }

  const card = invoice.paymentCard || {};
  const cardType = (card.type || '').toUpperCase();

  const fee = resolveFeeCents({ invoice, surchargeCents, baseCents, cardType, settings });
  const feeCents = fee.cents;

  // Net to merchant = gross charged minus the platform billing fee pulled
  // from it. For credit-surcharge that equals base (fee == surcharge); for
  // debit it's base − 0.5%; for profile-rate it's charged − reported fee.
  const netCents = feeCents == null ? null : chargedCents - feeCents;

  const paidDate = parseBackendDate(extractChargeAt(invoice));

  return {
    id: invoice.id,
    patientName: invoice.patientName || '—',
    paidAt: invoice.paidAt || null,
    paidDate,
    methodType: invoice.paymentMethodType || (card.last_4 ? 'CARD' : ''),
    cardBrand: card.brand_desc || card.brand || '',
    cardLast4: card.last_4 || '',
    cardType,
    baseCents,
    surchargeCents,
    surchargePct: surcharge.surchargePct,
    chargedCents,
    feeCents,
    feeSource: fee.source,
    feePct: fee.pct,
    netCents,
    payinId: invoice.payinId || '',
    status: (invoice.status || '').toLowerCase(),
    reconciliationStatus: (invoice.reconciliationStatus || 'pending').toLowerCase(),
  };
};

const formatDate = (date) => {
  if (!date) return '—';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (date) => {
  if (!date) return '';
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Local-date YYYY-MM-DD for <input type="date"> seeding.
const toInputDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const ReportTab = ({ practiceId }) => {
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // ALL | paid | processing
  const [cardTypeFilter, setCardTypeFilter] = useState('ALL'); // ALL | CREDIT | DEBIT

  // Default to month-to-date — the typical reconciliation window.
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return toInputDate(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [endDate, setEndDate] = useState(() => toInputDate(new Date()));

  const loadInvoices = useCallback(async () => {
    if (!practiceId) return;
    setLoading(true);
    setError('');
    try {
      // Pull the practice's surcharge / debit-billing settings alongside
      // the invoices so the fee math can apply the 0.5% debit override
      // even before the payin.processing webhook posts a fee.
      const [invoiceData, practiceData] = await Promise.all([
        fetchFromPaymentsAPI(`/api/payments/invoices?practiceId=${practiceId}`),
        getPracticeInfo(practiceId).catch(() => null),
      ]);

      // Same source the Settings tab reads — practice.payments carries the
      // configured ``debitCardBillingPct`` (or null) plus the server's
      // ``debitCardBillingPctDefault`` fallback.
      if (practiceData?.success && practiceData?.practice?.payments) {
        setSettings(practiceData.practice.payments);
      }

      if (invoiceData && invoiceData.success) {
        // Settled ("paid") invoices require a real ``paidAt`` settlement
        // timestamp — invoices flagged paid without one (legacy / non-card
        // artifacts) render as empty "—" rows that read as unpaid, so we
        // exclude them. In-flight ("processing") invoices have no paidAt
        // yet (it's stamped on payin.succeeded), so we include them on the
        // status alone and date them by their charge/auth timestamp.
        const visible = (invoiceData.invoices || []).filter((inv) => {
          const s = (inv.status || '').toLowerCase();
          if (!REPORT_STATUSES.has(s)) return false;
          if (s === 'paid') return !!inv.paidAt;
          return true; // processing
        });
        setPaidInvoices(visible);
      } else {
        setError(invoiceData?.error || 'Failed to load invoices.');
        setPaidInvoices([]);
      }
    } catch (err) {
      console.error('Error loading report invoices:', err);
      setError('Failed to load invoices. Please check your connection.');
      setPaidInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const rows = useMemo(
    () => paidInvoices.map((inv) => toReportRow(inv, settings)),
    [paidInvoices, settings],
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const startMs = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null;
    const endMs = endDate ? new Date(`${endDate}T23:59:59.999`).getTime() : null;

    return rows
      .filter((row) => {
        if (startMs != null || endMs != null) {
          const ms = row.paidDate ? row.paidDate.getTime() : null;
          if (ms == null) return false;
          if (startMs != null && ms < startMs) return false;
          if (endMs != null && ms > endMs) return false;
        }
        if (statusFilter !== 'ALL' && row.status !== statusFilter) {
          return false;
        }
        if (cardTypeFilter !== 'ALL' && (row.cardType || '').toUpperCase() !== cardTypeFilter) {
          return false;
        }
        if (term) {
          const haystack = `${row.patientName} ${row.id} ${row.cardLast4} ${row.payinId}`.toLowerCase();
          if (!haystack.includes(term)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const at = a.paidDate ? a.paidDate.getTime() : 0;
        const bt = b.paidDate ? b.paidDate.getTime() : 0;
        return bt - at;
      });
  }, [rows, search, startDate, endDate, statusFilter, cardTypeFilter]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        acc.count += 1;
        acc.baseCents += row.baseCents;
        acc.surchargeCents += row.surchargeCents;
        acc.chargedCents += row.chargedCents;
        acc.feeCents += row.feeCents || 0;
        acc.feeKnown = acc.feeKnown && row.feeCents != null;
        // Unknown-fee rows fall back to net == charged (fee assumed $0),
        // matching the `*` footnote so the column still foots.
        acc.netCents += row.netCents == null ? row.chargedCents : row.netCents;
        // Split the charged total by settlement status so non-surcharging
        // practices can see settled ("Paid") vs in-flight ("Processing").
        if (row.status === 'processing') {
          acc.processingCents += row.chargedCents;
          acc.processingCount += 1;
        } else {
          acc.paidCents += row.chargedCents;
          acc.paidCount += 1;
        }
        return acc;
      },
      {
        count: 0, baseCents: 0, surchargeCents: 0, chargedCents: 0, feeCents: 0, feeKnown: true, netCents: 0,
        paidCents: 0, paidCount: 0, processingCents: 0, processingCount: 0,
      },
    );
  }, [filteredRows]);

  // When the practice doesn't surcharge, the "Surcharge" column is always
  // $0 and useless — swap it for the processing fee the merchant actually
  // pays so the report stays informative.
  const surchargeEnabled = !!settings?.surchargeEnabled;

  const handleExportCsv = useCallback(() => {
    const headers = [
      'Date',
      'Status',
      'Invoice ID',
      'Patient',
      'Card Brand',
      'Card Last 4',
      'Card Type',
      'Invoice Amount',
      'Surcharge',
      'Total Charged',
      'Processing Fee',
      'Net to Merchant',
      'Payin ID',
    ];
    const escape = (val) => {
      const s = val == null ? '' : String(val);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const fmt = (cents) => ((Number(cents) || 0) / 100).toFixed(2);
    const lines = [headers.join(',')];
    filteredRows.forEach((row) => {
      lines.push(
        [
          row.paidAt || (row.paidDate ? row.paidDate.toISOString() : ''),
          (statusLabels[row.status] || {}).label || row.status,
          row.id,
          row.patientName,
          row.cardBrand,
          row.cardLast4,
          row.cardType,
          fmt(row.baseCents),
          fmt(row.surchargeCents),
          fmt(row.chargedCents),
          row.feeCents == null ? '' : fmt(row.feeCents),
          row.netCents == null ? '' : fmt(row.netCents),
          row.payinId,
        ]
          .map(escape)
          .join(','),
      );
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reconciliation_${practiceId}_${startDate}_to_${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [filteredRows, practiceId, startDate, endDate]);

  const summaryTiles = [
    { label: 'Payments', value: totals.count.toLocaleString('en-US'), color: '#1f5a3d' },
    { label: 'Invoice Amount', value: usd(totals.baseCents), color: '#1d6b40' },
    // Surcharge Collected is always $0 for non-surcharging practices — hide it.
    ...(surchargeEnabled
      ? [{ label: 'Surcharge Collected', value: usd(totals.surchargeCents), color: '#5b3fa8' }]
      : []),
    ...(surchargeEnabled
      ? [{ label: 'Total Charged', value: usd(totals.chargedCents), color: '#1d6b40' }]
      : [
          { label: 'Paid', value: usd(totals.paidCents), color: '#1d6b40' },
          { label: 'Processing', value: usd(totals.processingCents), color: '#a8520c' },
        ]),
    {
      label: 'Processing Fees',
      value: totals.feeKnown ? usd(totals.feeCents) : `${usd(totals.feeCents)}*`,
      color: '#b42318',
    },
    {
      label: 'Net to Merchant',
      value: totals.feeKnown ? usd(totals.netCents) : `${usd(totals.netCents)}*`,
      color: '#1d6b40',
    },
  ];

  const inputStyle = {
    padding: '8px 10px',
    border: '1px solid #d6dbd7',
    borderRadius: '6px',
    fontSize: '0.9rem',
  };

  const thStyle = {
    textAlign: 'left',
    padding: '10px 12px',
    fontSize: '0.78rem',
    fontWeight: 700,
    color: '#3c4540',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    borderBottom: '2px solid #e3e7e4',
    whiteSpace: 'nowrap',
  };
  const thRight = { ...thStyle, textAlign: 'right' };
  const tdStyle = {
    padding: '10px 12px',
    fontSize: '0.9rem',
    color: '#1a1d1b',
    borderBottom: '1px solid #eef0ee',
    whiteSpace: 'nowrap',
  };
  const tdRight = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <div>
      {error && (
        <p style={{ color: '#b42318', marginBottom: '20px' }}>⚠️ {error}</p>
      )}

      {/* Summary tiles */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '12px',
          marginBottom: '20px',
        }}
      >
        {summaryTiles.map((tile) => (
          <div
            key={tile.label}
            style={{
              backgroundColor: 'white',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
            }}
          >
            <div style={{ fontSize: '0.78rem', color: '#6b716d', marginBottom: '6px' }}>
              {tile.label}
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: tile.color }}>
              {tile.value}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div
        style={{
          backgroundColor: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          marginBottom: '20px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'flex-end',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '0.75rem', color: '#6b716d' }}>Date Range</label>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => { setStartDate(start); setEndDate(end); }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '0.75rem', color: '#6b716d' }}>Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={inputStyle}>
            <option value="ALL">All</option>
            <option value="paid">Paid</option>
            <option value="processing">Processing</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '0.75rem', color: '#6b716d' }}>Card Type</label>
          <select value={cardTypeFilter} onChange={(e) => setCardTypeFilter(e.target.value)} style={inputStyle}>
            <option value="ALL">All</option>
            <option value="CREDIT">Credit</option>
            <option value="DEBIT">Debit</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '180px' }}>
          <label style={{ fontSize: '0.75rem', color: '#6b716d' }}>Search</label>
          <input
            type="text"
            placeholder="Patient, invoice ID, last 4…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={inputStyle}
          />
        </div>
        <button
          onClick={loadInvoices}
          style={{
            padding: '9px 16px',
            backgroundColor: '#eef0ee',
            color: '#1a1d1b',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: 600,
          }}
        >
          🔄 Refresh
        </button>
        <button
          onClick={handleExportCsv}
          disabled={filteredRows.length === 0}
          style={{
            padding: '9px 16px',
            backgroundColor: filteredRows.length === 0 ? '#a3a8a4' : '#1f5a3d',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: filteredRows.length === 0 ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem',
            fontWeight: 600,
          }}
        >
          ⬇️ Export CSV
        </button>
      </div>

      {/* Table */}
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          overflowX: 'auto',
        }}
      >
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b716d' }}>
            Loading payments…
          </div>
        ) : filteredRows.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b716d' }}>
            No payments match the selected filters.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '980px' }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Patient</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Card</th>
                <th style={thRight}>Invoice Amt</th>
                <th style={thRight}>{surchargeEnabled ? 'Surcharge' : 'Processing Fee'}</th>
                {surchargeEnabled && <th style={thRight}>Total Charged</th>}
                <th style={thRight}>Net to Merchant</th>
                <th style={thStyle}>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                return (
                  <tr key={row.id}>
                    <td style={tdStyle}>
                      <div>{formatDate(row.paidDate)}</div>
                      {row.paidDate && (
                        <div style={{ fontSize: '0.75rem', color: '#6b716d' }}>
                          {formatTime(row.paidDate)}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle} data-phi>{row.patientName}</td>
                    <td style={tdStyle}>
                      {(() => {
                        const st = statusLabels[row.status] || statusLabels.paid;
                        return (
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              backgroundColor: st.bg,
                              color: st.color,
                            }}
                          >
                            {st.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={tdStyle}>
                      {row.cardLast4 ? (
                        <div>
                          <div>{row.cardBrand ? `${row.cardBrand} ` : ''}•••• {row.cardLast4}</div>
                          {row.cardType && (
                            <span
                              style={{
                                display: 'inline-block',
                                marginTop: '3px',
                                padding: '1px 6px',
                                borderRadius: '4px',
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                backgroundColor: row.cardType === 'CREDIT' ? '#e6efe9' : '#f1ecfb',
                                color: row.cardType === 'CREDIT' ? '#174a31' : '#5b3fa8',
                              }}
                            >
                              {row.cardType}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#6b716d' }}>{row.methodType || '—'}</span>
                      )}
                    </td>
                    <td style={{ ...tdRight, fontWeight: 600 }}>{usd(row.baseCents)}</td>
                    {surchargeEnabled ? (
                      <td style={{ ...tdRight, color: row.surchargeCents ? '#5b3fa8' : '#a3a8a4' }}>
                        {row.surchargeCents ? usd(row.surchargeCents) : '—'}
                      </td>
                    ) : (
                      <td style={{ ...tdRight, color: row.feeCents ? '#b42318' : '#a3a8a4' }}>
                        {row.feeCents == null ? '—' : usd(row.feeCents)}
                      </td>
                    )}
                    {surchargeEnabled && (
                      <td style={{ ...tdRight, fontWeight: 600 }}>{usd(row.chargedCents)}</td>
                    )}
                    <td style={{ ...tdRight, fontWeight: 600, color: '#1d6b40' }}>
                      {row.netCents == null ? '—' : usd(row.netCents)}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8rem' }} title={row.payinId}>
                      {row.id}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #e3e7e4', fontWeight: 700 }}>
                <td style={tdStyle} colSpan={4}>
                  {totals.count} payment{totals.count === 1 ? '' : 's'}
                </td>
                <td style={tdRight}>{usd(totals.baseCents)}</td>
                <td style={tdRight}>
                  {surchargeEnabled
                    ? usd(totals.surchargeCents)
                    : (totals.feeKnown ? usd(totals.feeCents) : `${usd(totals.feeCents)}*`)}
                </td>
                {surchargeEnabled && <td style={tdRight}>{usd(totals.chargedCents)}</td>}
                <td style={{ ...tdRight, color: '#1d6b40' }}>
                  {totals.feeKnown ? usd(totals.netCents) : `${usd(totals.netCents)}*`}
                </td>
                <td style={tdStyle}></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {!loading && !totals.feeKnown && filteredRows.length > 0 && (
        <p style={{ marginTop: '12px', fontSize: '0.8rem', color: '#6b716d' }}>
          * Processing fees are not yet available for every payment in this range
          (fees post when NewtonPay moves the payin to <em>processing</em>). Rows
          without a fee assume $0.00 in the net total — use the Deposit Report for
          fully settled fee figures.
        </p>
      )}
    </div>
  );
};

export default ReportTab;
